import React from "react";
import type { ApolloClient } from "@apollo/client";
import { push } from "store/actions/router";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { connect, type ConnectedProps } from "react-redux";
import { get } from "lodash";
import { createErrorSelector } from "store/selectors";
import { employerConstants } from "actions/types";
import {
  getEmployeeDocumentUploadJobs,
  updateProgressiveOnboarding,
} from "actions/employerActions";
import { Card, Col, Row } from "react-bootstrap";
import { hasFullyOnboarded } from "store/selectors/employer";
import LottieAnimation from "components/LottieAnimation";

import Alert from "components/Alert";

import "./Employees.scss";
import PromisePoller from "components/PromisePoller";
import { progressiveOnboardingEvents } from "statics/states";
import type { RootState } from "types/state";

const actions = [employerConstants.GET_EMPLOYEE_DOCUMENT_UPLOAD_JOBS];

const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
    //OnBoardingSelectors
    hasFullyOnboarded: hasFullyOnboarded(state),
  };
};

const mapDispatchToProps = {
  push,
  getEmployeeDocumentUploadJobs,
  updateProgressiveOnboarding,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  onFinished?: (job: unknown, hasErrored: boolean) => void;
  companyId?: string;
  heading?: string;
  subheading?: string;
  onCancel?: (...args: unknown[]) => void;
  onSuccess?: (...args: unknown[]) => void;
}

type Props = WithApolloClient<OwnProps> & PropsFromRedux;

interface State {
  showForm: boolean;
}

class UploadProcessing extends React.Component<Props, State> {
  static defaultProps = {
    heading: "Processing File",
    subheading: "Give us a moment while we process your file.",
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      showForm: false,
    };
  }

  verify = async () => {
    const uploadJobs = await this.props.getEmployeeDocumentUploadJobs(
      this.props.client as ApolloClient<object>,
      this.props.companyId as string
    );

    const status: string | undefined = get(uploadJobs, "data.[0].status");
    const terminalStatuses = ["PROCESSED", "ERROR"];
    // includes() narrows to string; status may be undefined at runtime, which
    // .includes() handles (returns false) — preserve that, only the type is cast.
    const isTerminalState = terminalStatuses.includes(status as string);
    const isProcessed = status === "PROCESSED";

    const job = get(uploadJobs, "data.[0]");
    const hasErrored = status === "ERROR";

    if (isProcessed && !this.props.hasFullyOnboarded) {
      // Pre-existing quirk preserved: updateProgressiveOnboarding's action
      // creator requires a third `companyId` arg, but this call site has always
      // omitted it. Keep the 2-arg call to preserve runtime behavior exactly.
      // @ts-expect-error missing third companyId arg is a pre-existing bug, preserved verbatim
      void this.props.updateProgressiveOnboarding(
        this.props.client as ApolloClient<object>,
        progressiveOnboardingEvents.InviteUsers
      );
      if (this.props.onFinished) {
        this.props.onFinished(job, hasErrored);
      }
    } else if (isTerminalState && this.props.onFinished) {
      this.props.onFinished(job, hasErrored);
    }
    return status;
  };

  render() {
    return (
      <div className="download-employee-template">
        <PromisePoller
          interval={4000}
          minutesToPollFor={10}
          promiseToPoll={this.verify}
        />
        <div className="main-content">
          <Row>
            <Col>
              <Card>
                <Card.Title>
                  <p className="page-header">{this.props.heading}</p>
                  <p className="page-subtext">{this.props.subheading}</p>
                </Card.Title>
                <Card.Body>
                  <LottieAnimation />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

        {this.props.error && <Alert type="error" msg={this.props.error} />}
      </div>
    );
  }
}

export default connector(withApollo<Props>(UploadProcessing));
